const TL_CODES = ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m']

const KEAP_ADDON_CODES = [
  'addon-5-keap-addons',
  'addon-5-keap-addons-annual',
  'addon-5-keap-addons-legacy-annual',
  'addon-5-keap-addons-legacy-m2m',
]

function formatSub(product) {
  if (product.showZeroPrice) return `$${product.recurringPrice}/mo`
  // FULSAS-14552: on Due Today, an amount that differs from the list price
  // (coupon or proration) is a one-off charge, so the line under the item name
  // shows the item's real monthly amount (list price) instead of that charge
  // mislabelled "/mo" — the incentive is shown separately on the price side.
  const price = product.dueTodayDiffersFromRecurring
    ? product.recurringPrice
    : product.unitPrice ?? product.price
  if (price === 0) return 'Included'
  if (product.billingFrequency === 'monthly') return `$${price}/mo`
  return 'One-time'
}

function rankAddon(a) {
  const code = a.productCode ?? a.productId
  const name = a.productName ?? a.name
  if (TL_CODES.includes(code) || code === 'seobase-5-thryv-m2m') return 1
  if (code === 'appmarket-5-thryv-m2m') return 2
  if (KEAP_ADDON_CODES.includes(code)) return 4
  if (name?.toLowerCase().includes('keap')) return 5
  return 3
}

// Country-aware list of software rows always shown in the order summary,
// Rows are placeholders (price: null) until the user selects a plan,
// except My Account and Reporting Center which are always free.
function buildSoftwarePlaceholders(selectedCountry) {
  const rows = [
    {
      productId: 'my-account',
      name: 'My Account',
      price: 0,
      type: 'software',
      isAutoAdded: true,
    },
    {
      productId: 'business-center',
      name: 'Business Center',
      price: null,
      type: 'software',
      isAutoAdded: false,
    },
  ]
  if (selectedCountry !== 'KY' && selectedCountry !== 'BB') {
    rows.push({
      productId: 'marketing-center',
      name: 'Marketing Center',
      price: null,
      type: 'software',
      isAutoAdded: false,
    })
  }
  rows.push({
    productId: 'reporting-center',
    name: 'Reporting Center',
    price: null,
    type: 'software',
    isAutoAdded: false,
  })
  rows.push({
    productId: 'keap-max',
    name: 'Keap Max',
    price: null,
    type: 'software',
    isAutoAdded: false,
  })
  if (selectedCountry === 'US') {
    rows.push({
      productId: 'workforce-center',
      name: 'Workforce Center',
      price: null,
      type: 'software',
      isAutoAdded: false,
    })
  }
  return rows
}

// Merges defaultPlanConfig from the API with the country-aware placeholder list.
// centers (software) and apps (addons with alreadyIncluded) override placeholder
// defaults where names match; unmatched API centers are appended.
export function buildIncludedFromPlanConfig(
  defaultPlanConfig,
  selectedCountry,
) {
  const placeholders = buildSoftwarePlaceholders(selectedCountry)

  const centersByName = {}
  ;(defaultPlanConfig?.centers ?? []).forEach(c => {
    centersByName[c.name?.toLowerCase()] = c
  })

  const includedProducts = placeholders.map(p => {
    const apiCenter = centersByName[p.name?.toLowerCase()]
    if (apiCenter) {
      return {
        ...p,
        price: apiCenter.amount ?? 0,
        isAutoAdded: (apiCenter.amount ?? 0) === 0,
      }
    }
    return p
  })

  // Append any API centers not covered by the placeholder list
  const coveredNames = new Set(placeholders.map(p => p.name?.toLowerCase()))
  ;(defaultPlanConfig?.centers ?? [])
    .filter(c => !coveredNames.has(c.name?.toLowerCase()))
    .forEach(c => {
      includedProducts.push({
        productId: c.code,
        name: c.name,
        price: c.amount ?? 0,
        type: 'software',
        isAutoAdded: (c.amount ?? 0) === 0,
      })
    })

  const includedAddons = (defaultPlanConfig?.apps ?? [])
    .filter(a => a.alreadyIncluded === true)
    .map(a => ({
      productId: a.code,
      name: a.name,
      price: a.amount ?? 0,
      type: 'addon',
      isAutoAdded: true,
    }))

  return {includedProducts, includedAddons}
}

// One row per included default in order, filled from selectedSoftware when matched.
// Unmatched free defaults show "Included"; unmatched placeholders show empty.
// Extra selected software outside the defaults appended at the end.
export function buildSoftwareItems(includedProducts, selectedSoftware) {
  const selectedById = {}
  selectedSoftware.forEach(p => {
    selectedById[p.productCode ?? p.productId] = p
  })

  const defaultRows = includedProducts.map(ip => {
    const ipCode = ip.productCode ?? ip.productId
    const ipName = ip.productName ?? ip.name
    const sel =
      selectedById[ipCode] ??
      selectedSoftware.find(p =>
        (p.productName ?? p.name).toLowerCase().includes(ipName.toLowerCase()),
      )
    if (sel) {
      const price = sel.unitPrice ?? sel.price
      const name = sel.productName ?? sel.name
      return {...sel, name, price, sub: formatSub(sel), selected: price > 0}
    }
    if (ip.isAutoAdded && ip.price === 0) {
      return {
        productId: ipCode,
        name: ipName,
        sub: 'Included',
        price: 0,
        selected: false,
      }
    }
    return {
      productId: ipCode,
      name: ipName,
      sub: '',
      price: null,
      selected: false,
    }
  })

  const extra = selectedSoftware
    .filter(
      p =>
        !includedProducts.some(ip => {
          const ipCode = ip.productCode ?? ip.productId
          const ipName = ip.productName ?? ip.name
          const pCode = p.productCode ?? p.productId
          const pName = p.productName ?? p.name
          return (
            ipCode === pCode ||
            pName.toLowerCase().includes(ipName.toLowerCase())
          )
        }),
    )
    .map(p => {
      const price = p.unitPrice ?? p.price
      const name = p.productName ?? p.name
      return {...p, name, price, sub: formatSub(p), selected: price > 0}
    })

  return [...defaultRows, ...extra]
}

// Included addons always shown first ($0, no highlight).
// Selected addons sorted by rank, with cross-out applied for conflicting items.
// Mirrors RenderItems logic from old order-summary.js.
export function buildAddonItems(
  includedAddons,
  selectedAddons,
  selectedSoftware,
  // the Growth Package cross-out only applies when the TL_MC_Access +
  // TL_Tax_Plan_Enabled flag combo (see getIsNewTLMCAccessible) is on.
  isNewTLMCAccessible = false,
) {
  const isNewSEO = selectedAddons.some(
    a => (a.productCode ?? a.productId) === 'seobase-5-thryv-m2m',
  )
  const isTLInCart =
    selectedAddons.some(a => TL_CODES.includes(a.productCode ?? a.productId)) ||
    selectedSoftware.some(s => TL_CODES.includes(s.productCode ?? s.productId))
  const isBCUnlimited = selectedSoftware.some(
    s => (s.productCode ?? s.productId) === 'bc_unlm-5-thryv-m2m',
  )
  const isBCorMCInCart = selectedSoftware.some(s => {
    const code = s.productCode ?? s.productId
    return code?.startsWith('bc_') || code?.startsWith('mc_')
  })

  const resolved = selectedAddons
    .map(a => {
      const code = a.productCode ?? a.productId
      const rawName = a.productName ?? a.name
      const price = a.unitPrice ?? a.price

      const crossedOut =
        (['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(code) &&
          isNewSEO) ||
        (['Expanded Growth Package', 'Starter Growth Package'].includes(
          rawName,
        ) &&
          isTLInCart &&
          isNewTLMCAccessible) ||
        (code === 'addon-5-mktproeditor' && isBCUnlimited) ||
        (code === 'app-listings' && isBCorMCInCart)

      return {
        ...a,
        price,
        name: rawName,
        productName: rawName,
        sub: formatSub(a),
        selected: price > 0,
        crossedOut,
      }
    })
    .sort((a, b) => rankAddon(a) - rankAddon(b))

  return [
    ...includedAddons.map(a => ({
      ...a,
      sub: 'Included',
      selected: false,
      crossedOut: false,
    })),
    ...resolved,
  ]
}

const REMOVABLE_ONE_TIME_FEE_CODES = [
  'thryvlogo4',
  'addon-5-keap-qklaunch-ot',
  'addon-5-keap-askexpt-ot',
  'thryv5page',
  'addon-5-keap-custdfy-ot',
]

// Shared by PurchaseLink's/ProposalTool's OrderSummaryContainer (single
// active location) and the parent-pay flow's multi-location container (one
// call per included location) — keeps the due-today pricing rules
// (proration overrides, one-time-fee shaping, per-tab totals/counts) from
// drifting between the two call sites.
// items: a PurchaseLinkSlice/ProposalSlice location's `items` array.
export function buildLocationOrderSummary(
  items = [],
  {isMonthlyTab = false, isNewTLMCAccessible = false} = {},
) {
  const selectedSoftware = items.filter(p => p.productType === 'CENTER')
  const selectedAddons = items.filter(p => p.productType === 'APP')
  const selectedOneTimeFees = items.filter(
    p => p.productType === 'ONE_TIME_FEE',
  )

  const visibleSoftware = isMonthlyTab
    ? selectedSoftware.filter(p => p.billingFrequency !== 'annual')
    : selectedSoftware
  const visibleAddons = isMonthlyTab
    ? selectedAddons.filter(p => p.billingFrequency !== 'annual')
    : selectedAddons

  // On the "Due Today" tab, items (and nested add-ons) the account already
  // owns are listed but priced at $0, which the renderer shows as "Included" —
  // they are recurring only, nothing is charged for them today. The monthly
  // tab keeps their real price. Totals are computed from `items` below, so
  // this only affects what each row displays.
  const markIncludedForToday = a => {
    if (isMonthlyTab) return a
    const addons = a.addons?.length
      ? a.addons.map(addon =>
          addon.alreadyIncluded ? {...addon, unitPrice: 0} : addon,
        )
      : a.addons
    if (!a.alreadyIncluded) return addons === a.addons ? a : {...a, addons}
    return {...a, unitPrice: 0, price: 0, addons}
  }

  // On the "Due Today" tab, override unitPrice with dueTodayAmount (the
  // Recurly-computed prorated amount) for non-included items and their
  // nested addons. quantity is normalized to 1 because dueTodayAmount is
  // already the total charge. On the monthly tab the catalog unitPrice is
  // kept as-is.
  const withDueTodayPrices = a => {
    // A newly added item that costs $0 today but has a real recurring price
    // (e.g. a ramp plan whose first cycle is free) is a $0 charge, not a
    // bundled freebie — flag it so the renderer shows "$0.00" instead of
    // "Included" and keeps it removable.
    const topLevel =
      !isMonthlyTab && !a.alreadyIncluded && a.dueTodayAmount != null
        ? {
            ...a,
            unitPrice: a.dueTodayAmount,
            ...(a.dueTodayAmount > 0 &&
              Number(a.unitPrice) > 0 &&
              Number(a.dueTodayAmount) !== Number(a.unitPrice) && {
                dueTodayDiffersFromRecurring: true,
                recurringPrice: a.unitPrice,
              }),
            ...(a.dueTodayAmount === 0 &&
              Number(a.unitPrice) > 0 && {
                showZeroPrice: true,
                recurringPrice: a.unitPrice,
              }),
          }
        : a
    if (!topLevel.addons?.length) return topLevel
    return {
      ...topLevel,
      addons: topLevel.addons.map(addon =>
        !isMonthlyTab &&
        !addon.alreadyIncluded &&
        addon.dueTodayAmount !== undefined
          ? {...addon, unitPrice: addon.dueTodayAmount, quantity: 1}
          : addon,
      ),
    }
  }

  const softwareItems = buildSoftwareItems(
    [],
    visibleSoftware.map(withDueTodayPrices).map(markIncludedForToday),
  )

  const addonItems = buildAddonItems(
    [],
    visibleAddons.map(withDueTodayPrices).map(markIncludedForToday),
    visibleSoftware,
    isNewTLMCAccessible,
  ).map(item =>
    item.productCode === 'kp-zapier-750zaps-m2m'
      ? {...item, removable: true}
      : item,
  )

  // For existing clients dueTodayAmount is the Recurly-computed proration
  // amount (already coupon-adjusted, set by the payment-preview hook via
  // cc/order/preview-payment). For new clients it is unitPrice minus any
  // coupon discount (set by that same hook's client-side path). Fall back to
  // unitPrice only when neither value is available yet (e.g. a product was
  // just added before the next preview refresh fires). Nested addons (e.g.
  // SEO PowerBoost/Keywords under seobase) are included so their preview
  // amounts are reflected in the due-today total.
  const newItems = items.filter(p => !p.alreadyIncluded)
  const total = newItems.reduce(
    (sum, p) =>
      sum +
      Number(p.dueTodayAmount ?? p.unitPrice ?? 0) +
      (p.addons ?? []).reduce(
        (aSum, addon) =>
          aSum +
          (!addon.alreadyIncluded
            ? Number(addon.dueTodayAmount ?? addon.unitPrice ?? 0)
            : 0),
        0,
      ),
    0,
  )

  const monthlyItems = items.filter(p => p.billingFrequency !== 'annual')
  const visibleItems = isMonthlyTab ? monthlyItems : items

  const monthlyTotal = monthlyItems
    .filter(p => p.productType !== 'ONE_TIME_FEE')
    .reduce(
      (sum, p) =>
        sum +
        Number(p.unitPrice ?? 0) +
        (p.addons ?? []).reduce(
          (aSum, addon) =>
            aSum + (!addon.alreadyIncluded ? Number(addon.unitPrice ?? 0) : 0),
          0,
        ),
      0,
    )

  const tabItemCounts = {
    today: newItems.length,
    monthly: monthlyItems.filter(p => p.productType !== 'ONE_TIME_FEE').length,
  }

  const tabTotals = {
    today: total,
    monthly: monthlyTotal,
  }

  const oneTimeFeeItems = isMonthlyTab
    ? []
    : selectedOneTimeFees.map(p => ({
        name: p.productName,
        price: p.dueTodayAmount ?? p.unitPrice,
        productCode: p.productCode,
        removable: REMOVABLE_ONE_TIME_FEE_CODES.includes(p.productCode),
        selected: false,
        crossedOut: false,
      }))

  return {
    softwareItems,
    addonItems,
    oneTimeFeeItems,
    itemCount: visibleItems.length,
    newItemCount: newItems.length,
    total,
    monthlyTotal,
    tabItemCounts,
    tabTotals,
  }
}

// sharedIncluded: { includedProducts, includedAddons } derived from defaultPlanConfig.
// When provided, overrides the per-location values stored in Redux state.
export function buildLocations(
  selectionsByLocation,
  locationIds,
  sharedIncluded,
) {
  return locationIds
    .map(id => {
      const loc = selectionsByLocation[id]
      if (!loc) return null

      const includedProducts =
        sharedIncluded?.includedProducts ?? loc.includedProducts ?? []
      const includedAddons =
        sharedIncluded?.includedAddons ?? loc.includedAddons ?? []

      const allProducts = Object.values(loc.selectedProducts)
      const selectedSoftware = allProducts.filter(p => p.type === 'software')
      const selectedAddons = allProducts.filter(p => p.type === 'addon')

      return {
        locationId: loc.locationId,
        name: loc.locationName,
        softwareItems: buildSoftwareItems(includedProducts, selectedSoftware),
        addonItems: buildAddonItems(
          includedAddons,
          selectedAddons,
          selectedSoftware,
        ),
        itemCount: allProducts.length,
        total: allProducts.reduce((sum, p) => sum + Number(p.price ?? 0), 0),
      }
    })
    .filter(Boolean)
}
